import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { LayoutDashboard, Users, Car, Bike, BarChart3, LogOut, Shield, Settings, DollarSign, Wallet, Bell, X, UserPlus, CreditCard, XCircle, Activity, Truck, Bus, Zap, Tag, Heart, MessageSquare, Megaphone, Globe, Rocket, BarChart } from 'lucide-react';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/notifications?limit=10');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/notifications/unread-count');
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/admin/notifications/mark-read');
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    setShowNotifications(false);
    switch (notification.type) {
      case 'new_driver_application':
        navigate('/admin/drivers');
        break;
      case 'withdrawal_request':
        navigate('/admin/wallets');
        break;
      case 'new_user_signup':
        navigate('/admin/users');
        break;
      case 'ride_cancelled':
        navigate('/admin/rides');
        break;
      default:
        navigate('/admin/dashboard');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_driver_application':
        return <Car className="h-5 w-5 text-white" />;
      case 'withdrawal_request':
        return <CreditCard className="h-5 w-5 text-white" />;
      case 'new_user_signup':
        return <UserPlus className="h-5 w-5 text-white" />;
      case 'ride_cancelled':
        return <XCircle className="h-5 w-5 text-white" />;
      default:
        return <Bell className="h-5 w-5 text-white" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'new_driver_application':
        return 'bg-gradient-to-br from-pink-500 to-purple-600';
      case 'withdrawal_request':
        return 'bg-gradient-to-br from-orange-500 to-amber-600';
      case 'new_user_signup':
        return 'bg-gradient-to-br from-green-500 to-emerald-600';
      case 'ride_cancelled':
        return 'bg-gradient-to-br from-red-500 to-rose-600';
      default:
        return 'bg-gray-500';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tekstar_token');
    localStorage.removeItem('tekstar_user');
    localStorage.removeItem('is_admin');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/drivers', icon: Car, label: 'Drivers' },
    { path: '/admin/bikers', icon: Bike, label: 'Bikers' },
    { path: '/admin/rides', icon: Car, label: 'Rides' },
    { path: '/admin/bus', icon: Bus, label: 'Bus Booking' },
    { path: '/admin/logistics', icon: Truck, label: 'Logistics' },
    { path: '/admin/wallets', icon: Wallet, label: 'Wallets' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/pricing', icon: DollarSign, label: 'Pricing' },
    { path: '/admin/surge', icon: Zap, label: 'Surge Pricing' },
    { path: '/admin/promos', icon: Tag, label: 'Promo Codes' },
    { path: '/admin/winback', icon: Heart, label: 'Win-back' },
    { path: '/admin/settlements', icon: DollarSign, label: 'Settlements' },
    { path: '/admin/ops-live', icon: Activity, label: 'Live Ops' },
    { path: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
    { path: '/admin/whatsapp/analytics', icon: BarChart, label: 'WhatsApp Analytics' },
    { path: '/admin/bus-refunds', icon: Wallet, label: 'Bus Refunds' },
    { path: '/admin/bus-reminders', icon: Bell, label: 'Bus Reminders' },
    { path: '/admin/push-broadcast', icon: Megaphone, label: 'Push Broadcast' },
    { path: '/admin/cities', icon: Globe, label: 'Cities' },
    { path: '/admin/city-waitlist', icon: Rocket, label: 'Launch Waitlist' },
    { path: '/admin/monitoring', icon: Activity, label: 'Monitoring' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tekstar Admin</h1>
                <p className="text-xs text-gray-500">Administration Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-sm text-pink-600 hover:text-pink-700"
                          >
                            Mark all read
                          </button>
                        )}
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                          <p>No notifications</p>
                        </div>
                      ) : (
                        notifications.map((notification, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleNotificationClick(notification)}
                            className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                              !notification.read ? 'bg-pink-50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-gray-900`}>
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(notification.createdAt).toLocaleString()}
                                </p>
                              </div>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-pink-50 text-pink-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;